import Login from '../pages/Login/Login'
import PipelineHistory from '../pages/PipelineHistory/PipelineHistory'
import Pipelines from '../pages/Pipelines/Pipelines'
import PipelineWizard from '../pages/PipelineWizard/PipelineWizard'
import Templates from '../pages/Template/Templates'
import CreateTemplate from '../pages/Template/CreateTemplate'
import TestTemplate from '../pages/Template/TestTemplate'
import { ROLE_SETS } from '../helpers/Constants'
import UploadTemplate from '../pages/Template/UploadTemplate'
import EditTemplate from '../pages/Template/EditTemplate'
import ApproveTemplate from '../pages/Template/ApproveTemplate'
import Reports from '../pages/Reports/Reports'
import Reputation from '../pages/Reputation/Reputation'
import BulkSend from '../pages/BulkSend/BulkSend'
import Settings from '../pages/Settings/Settings'
import TemplateWriter from '../pages/AI/TemplateWriter'
import CsvValidator from '../pages/AI/CsvValidator'
import BulkPreview from '../pages/AI/BulkPreview'
import CampaignBrief from '../pages/AI/CampaignBrief'
import PerformanceInsights from '../pages/AI/PerformanceInsights'
import ABVariants from '../pages/AI/ABVariants'
import UnsubscribeRisk from '../pages/AI/UnsubscribeRisk'
import AudienceOverlap from '../pages/AI/AudienceOverlap'
import CRMEnrich from '../pages/AI/CRMEnrich'
import CommsFinder from '../pages/AI/CommsFinder'
import AIIndex from '../pages/AI/AIIndex'
import Segments from '../pages/Segments/Segments'
import Guide from '../pages/Guide/Guide'
import NotFound from '../pages/NotFound/NotFound'
import Holidays from '../pages/Holidays/Holidays'
import CreateHoliday from '../pages/Holidays/CreateHoliday'
import BulkHolidays from '../pages/Holidays/BulkHolidays'
import HolidayManagerGate from '../pages/Holidays/HolidayManagerGate'
import CommsManagerGate from '../components/CommsManagerGate'
import TrainerNotifications from '../pages/TrainerNotifications/TrainerNotifications'
import TrainerNotificationForm from '../pages/TrainerNotifications/TrainerNotificationForm'
import TrainerNotificationDetail from '../pages/TrainerNotifications/TrainerNotificationDetail'
import LegacyRedirect from './LegacyRedirect'

/**
 * An address of the old comms app (served at the root until 2026-10-14)
 * that has another name in V2. No `roles`: the target route checks them.
 */
const legacy = (path, to) => ({
    path,
    element: <LegacyRedirect to={to} />,
    redirect: true,
})

/** A pipeline page that changes what gets sent: for the comms manager role only. */
const pipelineChange = (page) => (
    <CommsManagerGate
        title="You cannot change pipelines here"
        action="Creating and editing pipelines"
        elsewhere="Pipelines can only be changed on dev and production."
        backTo="/pipelines"
        backLabel="Back to pipelines"
    >
        {page}
    </CommsManagerGate>
)

/** A template page that changes what gets sent: for the comms manager role only. */
const templateChange = (page) => (
    <CommsManagerGate
        title="You cannot change templates here"
        action="Creating, uploading and editing templates"
        elsewhere="Templates can only be changed on dev and production."
        backTo="/templates"
        backLabel="Back to templates"
    >
        {page}
    </CommsManagerGate>
)

// Viewing needs the page's roles; changing what gets sent also needs
// comms-tool-dev/-prod (hooks/useCommsManager), as in the old comms app.
export default [
    {
        path: '/',
        element: <AIIndex />,
        roles: ROLE_SETS.everyone,
    },
    {
        path: '/login',
        element: <Login />,
    },
    {
        path: '/pipelines',
        element: <Pipelines />,
        roles: ROLE_SETS.operate,
    },
    {
        path: '/pipelines/new',
        element: pipelineChange(<PipelineWizard />),
        roles: ROLE_SETS.operate,
    },
    {
        path: '/pipelines/edit/:pipelineID',
        element: pipelineChange(<PipelineWizard />),
        roles: ROLE_SETS.operate,
    },
    // Viewing needs the Operate roles; changing a holiday also needs
    // comms-tool-dev/-prod, checked on the pages (helpers/holidays.js).
    {
        path: '/holidays',
        element: <Holidays />,
        roles: ROLE_SETS.operate,
    },
    {
        path: '/holidays/new',
        element: (
            <HolidayManagerGate>
                <CreateHoliday />
            </HolidayManagerGate>
        ),
        roles: ROLE_SETS.operate,
    },
    {
        path: '/holidays/bulk',
        element: (
            <HolidayManagerGate>
                <BulkHolidays />
            </HolidayManagerGate>
        ),
        roles: ROLE_SETS.operate,
    },
    {
        path: '/templates',
        element: <Templates />,
        roles: ROLE_SETS.studio,
    },
    {
        path: '/create-template',
        element: templateChange(<CreateTemplate />),
        roles: ROLE_SETS.studio,
    },
    {
        path: '/upload-template',
        element: templateChange(<UploadTemplate />),
        roles: ROLE_SETS.studio,
    },
    {
        path: '/edit-template',
        element: templateChange(<EditTemplate />),
        roles: ROLE_SETS.studio,
    },
    {
        path: '/approve-template/:template_id/:comm_type',
        element: <ApproveTemplate />,
        roles: ROLE_SETS.studio,
    },
    {
        path: '/test-template/:template_id/:template_name/:comm_type',
        element: <TestTemplate />,
        roles: ROLE_SETS.studio,
    },
    // Trainer dashboard notifications: viewing and changing both need
    // ROLE_SETS.trainerNotifications (admins for now), not the comms manager
    // role, so these pages are not behind CommsManagerGate.
    {
        path: '/trainer-notifications',
        element: <TrainerNotifications />,
        roles: ROLE_SETS.trainerNotifications,
    },
    {
        path: '/trainer-notifications/new',
        element: <TrainerNotificationForm />,
        roles: ROLE_SETS.trainerNotifications,
    },
    {
        path: '/trainer-notifications/:notificationId',
        element: <TrainerNotificationDetail />,
        roles: ROLE_SETS.trainerNotifications,
    },
    {
        path: '/trainer-notifications/:notificationId/edit',
        element: <TrainerNotificationForm />,
        roles: ROLE_SETS.trainerNotifications,
    },
    {
        path: '/pipeline/history',
        element: <PipelineHistory />,
        roles: ROLE_SETS.operate,
        routes: [
            {
                path: ':pipelineName',
                element: <PipelineHistory />,
                roles: ROLE_SETS.operate,
            },
        ],
    },
    {
        path: '/reports',
        element: <Reports />,
        roles: ROLE_SETS.operate,
    },
    {
        path: '/bulk-send',
        element: <BulkSend />,
        roles: ROLE_SETS.operate,
    },
    {
        path: '/ai',
        element: <AIIndex />,
        roles: ROLE_SETS.aiSuite,
    },
    {
        path: '/ai/template-writer',
        element: <TemplateWriter />,
        roles: ROLE_SETS.aiSuite,
    },
    {
        path: '/ai/csv-validator',
        element: <CsvValidator />,
        roles: ROLE_SETS.aiSuite,
    },
    {
        path: '/ai/bulk-preview',
        element: <BulkPreview />,
        roles: ROLE_SETS.aiSuite,
    },
    {
        path: '/ai/campaign-brief',
        element: <CampaignBrief />,
        roles: ROLE_SETS.aiSuite,
    },
    {
        path: '/ai/performance-insights',
        element: <PerformanceInsights />,
        roles: ROLE_SETS.aiSuite,
    },
    {
        path: '/ai/ab-variants',
        element: <ABVariants />,
        roles: ROLE_SETS.aiSuite,
    },
    {
        path: '/ai/unsubscribe-risk',
        element: <UnsubscribeRisk />,
        roles: ROLE_SETS.aiSuite,
    },
    {
        path: '/ai/audience-overlap',
        element: <AudienceOverlap />,
        roles: ROLE_SETS.aiSuite,
    },
    {
        path: '/ai/crm-enrich',
        element: <CRMEnrich />,
        roles: ROLE_SETS.aiSuite,
    },
    {
        path: '/ai/comms-finder',
        element: <CommsFinder />,
        roles: ROLE_SETS.aiSuite,
    },
    {
        path: '/reputation',
        element: <Reputation />,
        roles: ROLE_SETS.devAdmin,
    },
    {
        path: '/segments',
        element: <Segments />,
        roles: ROLE_SETS.devAdmin,
    },
    {
        path: '/settings',
        element: <Settings />,
        roles: ROLE_SETS.devAdmin,
    },
    // Every signed-in user, with or without a comms group.
    {
        path: '/guide',
        element: <Guide />,
        roles: ROLE_SETS.signedIn,
    },
    // Legacy deep links from v1 land on the wizard
    {
        path: '/event-selection',
        element: pipelineChange(<PipelineWizard />),
        roles: ROLE_SETS.operate,
        routes: [
            {
                path: 'edit/:pipelineID',
                element: pipelineChange(<PipelineWizard />),
                roles: ROLE_SETS.operate,
            },
        ],
    },
    // Links and bookmarks from the old comms app, before it moved to /old.
    legacy('/comms/sendbulk', '/bulk-send'),
    legacy('/pipelines/create', '/pipelines/new'),
    legacy('/templates/create', '/create-template'),
    legacy(
        '/templates/edit/:templateId',
        ({ templateId }) =>
            `/edit-template?template_id=${encodeURIComponent(templateId)}`
    ),
    legacy('/holidays/create', '/holidays/new'),
    legacy('/holidays/sendbulk', '/holidays/bulk'),
    // Anything unmatched. Must stay last only for readability: react-router
    // ranks `*` below every concrete path regardless of order.
    {
        path: '*',
        element: <NotFound />,
    },
]
